import { memo, useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SlidersHorizontal, Users, Columns3, Save, Loader2, Plus, X, Link2, Unlink, Gauge, Search, Route, Smartphone, ShieldPlus, PackageX } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { isActiveEmployee } from '../constants';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const NONE = '__none__';

const FORMULA_TYPES = [
  { value: 'fixed', label: 'Tarif fix (cantitate × tarif)' },
  { value: 'tiered', label: 'Tarif pe trepte' },
  { value: 'threshold', label: 'Bonus fix la prag' },
];

const LEVEL_TYPES = [
  { value: 'hd', label: 'HD (curieri)' },
  { value: 'ooh', label: 'OOH (lockere)' },
  { value: 'none', label: 'Niciunul' },
];

const matchAgent = (a, q) => {
  const s = q.trim().toLowerCase();
  return !s || (a.indicativ || '').toLowerCase().includes(s) || (a.name || '').toLowerCase().includes(s) || (a.employee_name || '').toLowerCase().includes(s);
};
const byAssoc = (a, mode) => mode === 'all' || (mode === 'assigned' ? !!a.employee_id : !a.employee_id);

const VISIBILITY = [
  { value: 'hidden', label: 'Ascuns', cls: 'text-slate-500' },
  { value: 'activity', label: 'Doar activitate', cls: 'text-blue-700' },
  { value: 'full', label: 'Cu calcul', cls: 'text-emerald-700' },
];

const empName = (u) => `${u.nume || ''} ${u.prenume || ''}`.trim() || u.name || u.username;

const FormulaEditor = memo(function FormulaEditor({ formula, onChange, disabled, testId }) {
  const f = formula || { type: 'fixed', rate: '' };
  const set = (patch) => onChange({ ...f, ...patch });
  const tiers = f.tiers || [{ from: 0, rate: '' }];
  const setTier = (i, patch) => set({ tiers: tiers.map((t, j) => (j === i ? { ...t, ...patch } : t)) });

  return (
    <div className="flex flex-col gap-2" data-testid={`${testId}-formula`}>
      <Select value={f.type} onValueChange={(type) => onChange({ type, ...(type === 'tiered' ? { tiers: [{ from: 0, rate: '' }], progressive: false } : {}) })} disabled={disabled}>
        <SelectTrigger className="h-8 w-56 text-xs" data-testid={`${testId}-formula-type`}><SelectValue /></SelectTrigger>
        <SelectContent>{FORMULA_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
      </Select>
      {f.type === 'fixed' && (
        <div className="flex items-center gap-2 text-xs">
          <Input type="number" step="0.01" className="h-8 w-24" placeholder="tarif" value={f.rate ?? ''} onChange={(e) => set({ rate: e.target.value })} disabled={disabled} data-testid={`${testId}-rate`} />
          <span className="text-slate-500">lei / unitate</span>
        </div>
      )}
      {f.type === 'threshold' && (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-500">dacă ≥</span>
          <Input type="number" step="0.01" className="h-8 w-20" placeholder="prag" value={f.threshold ?? ''} onChange={(e) => set({ threshold: e.target.value })} disabled={disabled} data-testid={`${testId}-threshold`} />
          <span className="text-slate-500">→</span>
          <Input type="number" step="0.01" className="h-8 w-24" placeholder="sumă" value={f.amount ?? ''} onChange={(e) => set({ amount: e.target.value })} disabled={disabled} data-testid={`${testId}-amount`} />
          <span className="text-slate-500">lei</span>
        </div>
      )}
      {f.type === 'tiered' && (
        <div className="flex flex-col gap-1 text-xs">
          {tiers.map((t, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-slate-500 w-10">{i === 0 ? 'de la' : 'peste'}</span>
              <Input type="number" step="1" className="h-8 w-20" value={t.from ?? ''} onChange={(e) => setTier(i, { from: e.target.value })} disabled={disabled || i === 0} data-testid={`${testId}-tier-${i}-from`} />
              <span className="text-slate-500">→</span>
              <Input type="number" step="0.01" className="h-8 w-24" placeholder="tarif" value={t.rate ?? ''} onChange={(e) => setTier(i, { rate: e.target.value })} disabled={disabled} data-testid={`${testId}-tier-${i}-rate`} />
              <span className="text-slate-500">lei/unit.</span>
              {i > 0 && !disabled && (
                <button type="button" onClick={() => set({ tiers: tiers.filter((_, j) => j !== i) })} className="text-slate-400 hover:text-red-600" data-testid={`${testId}-tier-${i}-remove`}><X className="w-3.5 h-3.5" /></button>
              )}
            </div>
          ))}
          {!disabled && (
            <div className="flex items-center gap-4 mt-1">
              <button type="button" onClick={() => set({ tiers: [...tiers, { from: '', rate: '' }] })} className="text-blue-600 hover:underline flex items-center gap-1" data-testid={`${testId}-tier-add`}><Plus className="w-3 h-3" /> treaptă</button>
              <label className="flex items-center gap-1.5 text-slate-600 cursor-pointer">
                <Checkbox checked={!!f.progressive} onCheckedChange={(v) => set({ progressive: !!v })} data-testid={`${testId}-progressive`} /> progresiv
              </label>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

const normalizeFormula = (f) => {
  if (!f) return null;
  const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
  if (f.type === 'fixed') return { type: 'fixed', rate: num(f.rate) };
  if (f.type === 'threshold') return { type: 'threshold', threshold: num(f.threshold), amount: num(f.amount) };
  if (f.type === 'tiered') return { type: 'tiered', progressive: !!f.progressive, tiers: (f.tiers || []).map((t) => ({ from: num(t.from) ?? 0, rate: num(t.rate) })) };
  return null;
};

const SetariMotivareTab = memo(function SetariMotivareTab({ canEdit = true }) {
  const [agents, setAgents] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [columns, setColumns] = useState([]);
  const [settings, setSettings] = useState({ min_days: 0, apply_deductions: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showInactive, setShowInactive] = useState(true);
  const [agentQuery, setAgentQuery] = useState('');
  const [agentMode, setAgentMode] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, e, c, s] = await Promise.all([
        axios.get(`${API_URL}/motivare/agents`, auth()),
        axios.get(`${API_URL}/incidents/employees-list`, auth()),
        axios.get(`${API_URL}/motivare/columns`, auth()),
        axios.get(`${API_URL}/motivare/settings`, auth()),
      ]);
      setAgents(a.data); setEmployees(e.data); setColumns(c.data); setSettings(s.data); setDirty(false);
    } catch { toast.error('Nu s-au putut încărca setările Motivare'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const assign = async (agent, employeeId) => {
    try {
      await axios.put(`${API_URL}/motivare/agents/${agent.id}`, { employee_id: employeeId === NONE ? null : employeeId }, auth());
      toast.success(employeeId === NONE ? `${agent.indicativ} dezasociat` : `${agent.indicativ} asociat`);
      const a = await axios.get(`${API_URL}/motivare/agents`, auth());
      setAgents(a.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare la asociere'); }
  };

  const setVisibility = async (agent, mode) => {
    try {
      await axios.put(`${API_URL}/motivare/agents/${agent.id}`, { mobile_visibility: mode }, auth());
      setAgents((list) => list.map((x) => (x.id === agent.id ? { ...x, mobile_visibility: mode } : x)));
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare la salvare'); }
  };

  const setAgentFlag = async (agent, field, val) => {
    try {
      await axios.put(`${API_URL}/motivare/agents/${agent.id}`, { [field]: val }, auth());
      setAgents((list) => list.map((x) => (x.id === agent.id ? { ...x, [field]: val } : x)));
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare la salvare'); }
  };
  const setBackup = (agent, val) => setAgentFlag(agent, 'backup', val);

  const setVisibilityAll = async (mode) => {
    if (!window.confirm(`Setezi „${VISIBILITY.find((v) => v.value === mode)?.label}” pentru toți cei ${agents.length} agenți?`)) return;
    try {
      await axios.put(`${API_URL}/motivare/agents-visibility`, { mode }, auth());
      setAgents((list) => list.map((x) => ({ ...x, mobile_visibility: mode })));
      toast.success('Vizibilitate actualizată pentru toți agenții');
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare la salvare'); }
  };

  const updateCol = (id, patch) => { setColumns((cols) => cols.map((c) => (c.id === id ? { ...c, ...patch } : c))); setDirty(true); };

  const saveAll = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/motivare/columns`, { columns: columns.map((c) => ({ id: c.id, active: !!c.active, formula: normalizeFormula(c.formula), level_type: c.level_type || 'hd' })) }, auth());
      const coefs = Object.fromEntries(Object.entries(settings.level_coefficients || {}).map(([k, v]) => [k, Number(v) || 0]));
      await axios.put(`${API_URL}/motivare/settings`, { min_days: Number(settings.min_days) || 0, apply_deductions: !!settings.apply_deductions, level_coefficients: coefs, unknown_level_coefficient: Number(settings.unknown_level_coefficient) || 0, km_active: !!settings.km_active, km_formula: normalizeFormula(settings.km_formula), km_level_type: settings.km_level_type || 'hd', mobile_history_enabled: true }, auth());
      toast.success('Setări salvate');
      setDirty(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare la salvare'); }
    finally { setSaving(false); }
  };

  const usedEmployees = new Set(agents.map((a) => a.employee_id).filter(Boolean));
  const unassigned = agents.filter((a) => !a.employee_id).length;
  const visibleAgents = agents.filter((a) => matchAgent(a, agentQuery) && byAssoc(a, agentMode));
  const activeCount = columns.filter((c) => c.active).length;
  const visibleColumns = showInactive ? columns : columns.filter((c) => c.active);

  if (loading) return <div className="flex items-center gap-2 text-slate-500 p-8" data-testid="setari-motivare-loading"><Loader2 className="w-4 h-4 animate-spin" /> Se încarcă...</div>;

  return (
    <div className="space-y-6" data-testid="setari-motivare-tab-content">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2"><SlidersHorizontal className="w-5 h-5 text-slate-600" /> Setări Motivare</h2>
          <p className="text-sm text-slate-500">Asociere agenți ↔ angajați, coloane active și formule de bonusare</p>
        </div>
        {canEdit && (
          <Button onClick={saveAll} disabled={saving || !dirty} data-testid="setari-motivare-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Salvează setările
          </Button>
        )}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><SlidersHorizontal className="w-4 h-4" /> Reguli globale</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-8">
          <div className="flex items-center gap-2">
            <Label htmlFor="min-days" className="text-sm">Zile lucrate minime pentru bonus</Label>
            <Input id="min-days" type="number" className="h-8 w-20" value={settings.min_days ?? 0} onChange={(e) => { setSettings({ ...settings, min_days: e.target.value }); setDirty(true); }} disabled={!canEdit} data-testid="setari-min-days" />
          </div>
          <div className="flex items-center gap-2">
            <Switch id="deductions" checked={!!settings.apply_deductions} onCheckedChange={(v) => { setSettings({ ...settings, apply_deductions: v }); setDirty(true); }} disabled={!canEdit} data-testid="setari-deductions-switch" />
            <Label htmlFor="deductions" className="text-sm">Scade ratele din Monitorizare Diferențe (luna respectivă)</Label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Gauge className="w-4 h-4" /> Coeficient pe Level</CardTitle>
          <CardDescription>Procent din bonusul brut acordat în funcție de nivel (aceeași grilă pentru Level HD și Level OOH). Curierii fără Level primesc coeficientul „necunoscut”. Coloanele OOH se calculează la 100% cât timp nu există raport Level OOH importat.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-4" data-testid="setari-level-coefficients">
          {['0', '1', '2', '3', '4', '5'].map((lv) => (
            <div key={lv} className="flex flex-col gap-1">
              <Label className="text-xs text-slate-600">{lv === '0' ? 'ZERO' : `Level ${lv}`}</Label>
              <div className="flex items-center gap-1">
                <Input type="number" min="0" max="200" className="h-8 w-20" value={settings.level_coefficients?.[lv] ?? ''} onChange={(e) => { setSettings({ ...settings, level_coefficients: { ...(settings.level_coefficients || {}), [lv]: e.target.value } }); setDirty(true); }} disabled={!canEdit} data-testid={`setari-level-coef-${lv}`} />
                <span className="text-xs text-slate-500">%</span>
              </div>
            </div>
          ))}
          <div className="flex flex-col gap-1 pl-4 border-l">
            <Label className="text-xs text-slate-600">Necunoscut</Label>
            <div className="flex items-center gap-1">
              <Input type="number" min="0" max="200" className="h-8 w-20" value={settings.unknown_level_coefficient ?? ''} onChange={(e) => { setSettings({ ...settings, unknown_level_coefficient: e.target.value }); setDirty(true); }} disabled={!canEdit} data-testid="setari-level-coef-unknown" />
              <span className="text-xs text-slate-500">%</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle className="text-base flex items-center gap-2"><Route className="w-4 h-4" /> Plată km</CardTitle>
              <CardDescription>Km din foile de parcurs (curse finalizate în luna calendaristică) ai angajatului asociat. Intră în Total ca orice altă coloană.</CardDescription>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <Switch checked={!!settings.km_active} onCheckedChange={(v) => { setSettings({ ...settings, km_active: v, km_formula: settings.km_formula || { type: 'fixed', rate: '' } }); setDirty(true); }} disabled={!canEdit} data-testid="setari-km-active" /> Activă
            </label>
          </div>
        </CardHeader>
        {settings.km_active && (
          <CardContent className="flex flex-wrap items-start gap-8" data-testid="setari-km-section">
            <div className="flex flex-col gap-1">
              <Label className="text-xs text-slate-600">Formulă (lei / km)</Label>
              <FormulaEditor formula={settings.km_formula} onChange={(f) => { setSettings({ ...settings, km_formula: f }); setDirty(true); }} disabled={!canEdit} testId="setari-km" />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-xs text-slate-600">Level aplicat</Label>
              <Select value={settings.km_level_type || 'hd'} onValueChange={(v) => { setSettings({ ...settings, km_level_type: v }); setDirty(true); }} disabled={!canEdit}>
                <SelectTrigger className="h-8 w-40 text-xs" data-testid="setari-km-level-type"><SelectValue /></SelectTrigger>
                <SelectContent>{LEVEL_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </CardContent>
        )}
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" /> Agenți din Excel ↔ Angajați</CardTitle>
          <CardDescription>
            {agents.length} agenți importați · <span className={unassigned ? 'text-amber-600 font-medium' : ''} data-testid="setari-unassigned-count">{unassigned} neasociați</span>. Agenții neasociați sunt ignorați la calcul.
          </CardDescription>
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-2 top-2" />
              <Input className="h-8 w-64 pl-8 text-sm" placeholder="Caută indicativ / nume..." value={agentQuery} onChange={(e) => setAgentQuery(e.target.value)} data-testid="setari-agent-search" />
            </div>
            <div className="flex rounded-md border overflow-hidden text-xs" data-testid="setari-agent-mode">
              {[['all', 'Toți'], ['assigned', 'Asociați'], ['unassigned', 'Neasociați']].map(([m, l]) => (
                <button key={m} type="button" onClick={() => setAgentMode(m)} className={`px-3 py-1.5 ${agentMode === m ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-testid={`setari-agent-mode-${m}`}>{l}</button>
              ))}
            </div>
            <span className="text-xs text-slate-500" data-testid="setari-agent-visible-count">{visibleAgents.length} afișați</span>
            {canEdit && (
              <div className="ml-auto flex items-center gap-2 text-xs text-slate-600">
                <Smartphone className="w-3.5 h-3.5" /> Setează pentru toți:
                {VISIBILITY.map((v) => (
                  <button key={v.value} type="button" onClick={() => setVisibilityAll(v.value)} className={`px-2 py-1 rounded border bg-white hover:bg-slate-50 ${v.cls}`} data-testid={`setari-visibility-all-${v.value}`}>{v.label}</button>
                ))}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {agents.length === 0 ? (
            <p className="text-sm text-slate-500 px-6 pb-6" data-testid="setari-no-agents">Niciun agent încă — importă primul Excel din tabul Motivare.</p>
          ) : (
            <Table data-testid="setari-agents-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Indicativ</TableHead>
                  <TableHead>Nume (din Excel)</TableHead>
                  <TableHead>Angajat în aplicație</TableHead>
                  <TableHead className="w-48">
                    <div className="flex items-center gap-1">
                      <Smartphone className="w-3.5 h-3.5" /> Aplicația mobilă
                    </div>
                  </TableHead>
                  <TableHead className="w-28">
                    <div className="flex items-center gap-1" title="Curier de back-up: primește automat un Level peste cel realizat">
                      <ShieldPlus className="w-3.5 h-3.5" /> Back-up
                    </div>
                  </TableHead>
                  <TableHead className="w-28">
                    <div className="flex items-center gap-1" title="Curierul vede în telefon lista AWB-urilor întârziate din ziua de Level">
                      <PackageX className="w-3.5 h-3.5" /> Vede AWB
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleAgents.map((a) => (
                  <TableRow key={a.id} data-testid={`agent-row-${a.indicativ}`}>
                    <TableCell>{a.employee_id ? <Link2 className="w-4 h-4 text-emerald-600" /> : <Unlink className="w-4 h-4 text-amber-500" />}</TableCell>
                    <TableCell className="font-mono text-sm">{a.indicativ}</TableCell>
                    <TableCell className="text-sm">{a.name}</TableCell>
                    <TableCell>
                      <Select value={a.employee_id || NONE} onValueChange={(v) => assign(a, v)} disabled={!canEdit}>
                        <SelectTrigger className="h-8 w-64 text-sm" data-testid={`agent-select-${a.indicativ}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NONE}><span className="text-slate-400">— neasociat —</span></SelectItem>
                          {employees.filter((u) => isActiveEmployee(u) || u.id === a.employee_id).map((u) => (
                            <SelectItem key={u.id} value={u.id} disabled={usedEmployees.has(u.id) && u.id !== a.employee_id}>{empName(u)}</SelectItem>
                          ))}
                          {a.employee_id && !employees.some((u) => u.id === a.employee_id) && (
                            <SelectItem value={a.employee_id}>{a.employee_name} <span className="text-slate-400">(fost angajat)</span></SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select value={a.mobile_visibility || 'hidden'} onValueChange={(v) => setVisibility(a, v)} disabled={!canEdit || !a.employee_id}>
                        <SelectTrigger className={`h-8 w-40 text-xs ${VISIBILITY.find((v) => v.value === (a.mobile_visibility || 'hidden'))?.cls}`} data-testid={`agent-visibility-${a.indicativ}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{VISIBILITY.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                        <Checkbox checked={!!a.backup} onCheckedChange={(v) => setBackup(a, !!v)} disabled={!canEdit} data-testid={`agent-backup-${a.indicativ}`} />
                        {a.backup ? <span className="text-violet-700 font-medium">+1 Level</span> : <span className="text-slate-400">—</span>}
                      </label>
                    </TableCell>
                    <TableCell>
                      <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                        <Checkbox checked={!!a.show_awb} onCheckedChange={(v) => setAgentFlag(a, 'show_awb', !!v)} disabled={!canEdit || !a.employee_id || (a.mobile_visibility || 'hidden') === 'hidden'} data-testid={`agent-show-awb-${a.indicativ}`} />
                        {a.show_awb ? <span className="text-red-700 font-medium">da</span> : <span className="text-slate-400">—</span>}
                      </label>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle className="text-base flex items-center gap-2"><Columns3 className="w-4 h-4" /> Coloane din Excel</CardTitle>
              <CardDescription>{columns.length} coloane detectate · <span data-testid="setari-active-count">{activeCount} active</span>. Doar coloanele active apar în Motivare și intră în calcul.</CardDescription>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <Switch checked={showInactive} onCheckedChange={setShowInactive} data-testid="setari-show-inactive" /> Arată și inactive
            </label>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {columns.length === 0 ? (
            <p className="text-sm text-slate-500 px-6 pb-6" data-testid="setari-no-columns">Nicio coloană încă — se detectează automat la primul import.</p>
          ) : (
            <Table data-testid="setari-columns-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Activă</TableHead>
                  <TableHead className="w-10">#</TableHead>
                  <TableHead>Coloană</TableHead>
                  <TableHead>Formulă</TableHead>
                  <TableHead className="w-44">Level aplicat</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleColumns.map((c) => (
                  <TableRow key={c.id} className={c.active ? '' : 'opacity-60'} data-testid={`column-row-${c.key}`}>
                    <TableCell><Checkbox checked={!!c.active} onCheckedChange={(v) => updateCol(c.id, { active: !!v, formula: c.formula || { type: 'fixed', rate: '' } })} disabled={!canEdit} data-testid={`column-active-${c.key}`} /></TableCell>
                    <TableCell className="text-xs text-slate-400">{c.order + 1}</TableCell>
                    <TableCell className="text-sm">{c.label}</TableCell>
                    <TableCell>
                      {c.active ? <FormulaEditor formula={c.formula} onChange={(f) => updateCol(c.id, { formula: f })} disabled={!canEdit} testId={`column-${c.key}`} /> : <span className="text-xs text-slate-400">—</span>}
                    </TableCell>
                    <TableCell>
                      {c.active ? (
                        <Select value={c.level_type || 'hd'} onValueChange={(v) => updateCol(c.id, { level_type: v })} disabled={!canEdit}>
                          <SelectTrigger className="h-8 w-40 text-xs" data-testid={`column-level-type-${c.key}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {LEVEL_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : <span className="text-xs text-slate-400">—</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
});

export default SetariMotivareTab;
